import React, { createContext, useContext, useState, ReactNode } from "react";

type ServiceTab = "mobile" | "digitalSecurity" | "fixedPhone" | null;

const FIXED_PHONE_DASHBOARD_SUB_TAB_SESSION_KEY =
  "fixedPhoneDashboardActiveSubTab";
const FIXED_PHONE_SIM_SERVICE_SUB_TAB_SESSION_KEY =
  "fixedPhoneSimServiceActiveSubTab";

function readPersistedSubTabIndex(storageKey: string): number {
  if (typeof window === "undefined") {
    return 0;
  }
  const n = Number(sessionStorage.getItem(storageKey));
  return Number.isInteger(n) && n >= 0 ? n : 0;
}

interface ServiceTabContextType {
  dashboardActiveTab: ServiceTab;
  simServiceActiveTab: ServiceTab;
  setDashboardActiveTab: (tab: ServiceTab) => void;
  setSimServiceActiveTab: (tab: ServiceTab) => void;
  fixedPhoneDashboardSubTabIndex: number;
  setFixedPhoneDashboardSubTabIndex: (index: number) => void;
  fixedPhoneSimServiceSubTabIndex: number;
  setFixedPhoneSimServiceSubTabIndex: (index: number) => void;
}

const ServiceTabContext = createContext<ServiceTabContextType | undefined>(
  undefined
);

export const ServiceTabProvider = ({ children }: { children: ReactNode }) => {
  const [dashboardActiveTab, setDashboardActiveTabState] = useState<ServiceTab>(
    () => {
      return (
        (sessionStorage.getItem("dashboardActiveTab") as ServiceTab) || null
      );
    }
  );
  const [simServiceActiveTab, setSimServiceActiveTabState] =
    useState<ServiceTab>(() => {
      return (
        (sessionStorage.getItem("simServiceActiveTab") as ServiceTab) || null
      );
    });

  const [fixedPhoneDashboardSubTabIndex, setFixedPhoneDashboardSubTabIndexState] =
    useState(() =>
      readPersistedSubTabIndex(FIXED_PHONE_DASHBOARD_SUB_TAB_SESSION_KEY)
    );

  const [fixedPhoneSimServiceSubTabIndex, setFixedPhoneSimServiceSubTabIndexState] =
    useState(() =>
      readPersistedSubTabIndex(FIXED_PHONE_SIM_SERVICE_SUB_TAB_SESSION_KEY)
    );

  const setDashboardActiveTab = (tab: ServiceTab) => {
    setDashboardActiveTabState(tab);
    if (tab) {
      sessionStorage.setItem("dashboardActiveTab", tab);
    } else {
      sessionStorage.removeItem("dashboardActiveTab");
    }
  };

  const setSimServiceActiveTab = (tab: ServiceTab) => {
    setSimServiceActiveTabState(tab);
    if (tab) {
      sessionStorage.setItem("simServiceActiveTab", tab);
    } else {
      sessionStorage.removeItem("simServiceActiveTab");
    }
  };

  const setFixedPhoneDashboardSubTabIndex = (index: number) => {
    const safe = Number.isInteger(index) && index >= 0 ? index : 0;
    setFixedPhoneDashboardSubTabIndexState(safe);
    sessionStorage.setItem(FIXED_PHONE_DASHBOARD_SUB_TAB_SESSION_KEY, String(safe));
  };

  const setFixedPhoneSimServiceSubTabIndex = (index: number) => {
    const safe = Number.isInteger(index) && index >= 0 ? index : 0;
    setFixedPhoneSimServiceSubTabIndexState(safe);
    sessionStorage.setItem(FIXED_PHONE_SIM_SERVICE_SUB_TAB_SESSION_KEY, String(safe));
  };

  return (
    <ServiceTabContext.Provider
      value={{
        dashboardActiveTab,
        simServiceActiveTab,
        setDashboardActiveTab,
        setSimServiceActiveTab,
        fixedPhoneDashboardSubTabIndex,
        setFixedPhoneDashboardSubTabIndex,
        fixedPhoneSimServiceSubTabIndex,
        setFixedPhoneSimServiceSubTabIndex,
      }}
    >
      {children}
    </ServiceTabContext.Provider>
  );
};

export const useServiceTab = () => {
  const context = useContext(ServiceTabContext);
  if (context === undefined) {
    throw new Error("useServiceTab must be used within a ServiceTabProvider");
  }
  return context;
};

export const DashboardTabProvider = ServiceTabProvider;
export const useDashboardTab = () => {
  const { dashboardActiveTab, setDashboardActiveTab } = useServiceTab();
  return { activeTab: dashboardActiveTab, setActiveTab: setDashboardActiveTab };
};

export const useSimServiceTab = () => {
  const { simServiceActiveTab, setSimServiceActiveTab } = useServiceTab();
  return {
    activeTab: simServiceActiveTab,
    setActiveTab: setSimServiceActiveTab,
  };
};
